import React, { useEffect, useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, Typography, Alert, CircularProgress,
  FormControl, FormLabel, RadioGroup, FormControlLabel, Radio,
} from '@mui/material'
import EuroOutlinedIcon from '@mui/icons-material/EuroOutlined'
import { FS, SP } from '../../theme/tokens'
import DisabledReason from '../../components/common/DisabledReason';

/**
 * ReturnCostDecisionDialog — feature-scoped RMA dialog. Collects the REASON for
 * a change of return-postage liability before the change is sent.
 *
 * @description
 *   Owner decision §E2 (2026-09-05): a free return is only free with EVIDENCE
 *   AND CONFIRMATION — either a customer email that clearly states it, or an
 *   explicit decision in the tool. `PUT /api/rma/cases/:id` enforces exactly
 *   that server-side and answers 422 (`return_cost_free_return_unproven`) when a
 *   caller tries to flip liability without both. This dialog is the human half
 *   of that contract: it does NOT re-implement the rule, it collects the two
 *   values the backend requires (`return_cost_decided_by`,
 *   `return_cost_reason`) so the request can succeed.
 *
 *   The reason list is filtered by DIRECTION. "Brewout pays" offers only the
 *   three §E1 reasons; "customer pays" offers the two that carry a customer's
 *   liability. Mixing them is rejected by the backend
 *   (`return_cost_reason_contradicts_payer`), so offering them would be
 *   offering a dead end.
 *
 *   NOT offered in either list: `unknown_default` and — since N81
 *   (2026-09-28) — the ground that DERIVES from the customer resolution. The
 *   first is the signature of the fallback ("nobody decided"), the second the
 *   signature of the derivation; the backend reserves both over this channel
 *   (`RETURN_COST_REASONS_RESERVED`), and a person opening this dialog IS
 *   deciding. Corrected 2026-09-29 (Rule-16 gate W9c): this head named only
 *   `unknown_default`, while the list had been missing a second entry since
 *   N81.
 *
 *   🔴 Der zweite Grund steht hier ABSICHTLICH ohne seinen Bezeichner: die
 *   Probe `test_p15b_der_dialog_bietet_ihn_nicht_die_anzeige_kennt_ihn`
 *   prueft, dass die rohe Kennung in DIESER Datei nicht vorkommt (der
 *   Textvergleich ist grob, aber er faengt jede versehentliche Aufnahme in
 *   eine der zwei Listen). Wer den Bezeichner hier ausschreibt, macht sie rot.
 *   Er steht dort, wo er hingehoert: in `returnCostProvenance.js`.
 *
 * @useWhen
 *   - Staff flips the return-type toggle and the change needs a documented
 *     reason before it is persisted
 *
 * @dontUseFor
 *   - Displaying the current liability — that is a read of
 *     `return_cost_reason` / `return_cost_decided_by` on the case payload
 *   - Any client-side pre-gate: the backend 422 is authoritative
 *
 * @props
 *   open         boolean — dialog visibility
 *   onClose      () => void — dismiss without changing anything
 *   customerPays boolean — the direction being MOVED TO (true = customer pays)
 *   onConfirm    (reason:string) => void — caller issues the PUT
 *   submitting   boolean — request in flight (disables inputs + confirm)
 *   errorMsg     string — error surfaced from a failed submit
 */

// Mirrors backend/rma/constants.py RETURN_COST_REASONS_*. Kept as a literal and
// not fetched: the backend rejects anything outside its own set, so a drift here
// surfaces as a 422 with a named reason rather than as a silent wrong write.
export const BREWOUT_PAYS_REASONS = [
  { value: 'defect_confirmed',
    label: 'Defect — confirmed',
    hint: 'Evidence seen AND confirmed. A customer simply choosing "defective" is not enough.' },
  { value: 'wrong_item_sent',
    label: 'Wrong item sent',
    hint: 'We shipped the wrong product.' },
  { value: 'failed_delivery_reship',
    label: 'Returned undelivered — reshipped as a gesture',
    hint: 'The parcel came back because it was never collected.' },
]

export const CUSTOMER_PAYS_REASONS = [
  { value: 'voluntary_return',
    label: 'Voluntary return',
    hint: 'Change of mind — the customer bears the return postage.' },
  { value: 'withdrawal',
    label: 'Withdrawal (Widerruf)',
    hint: '§ 357 (5) BGB — the customer bears the return postage.' },
]

export default function ReturnCostDecisionDialog({
  open, onClose, customerPays, onConfirm, submitting = false, errorMsg = '',
}) {
  const options = customerPays ? CUSTOMER_PAYS_REASONS : BREWOUT_PAYS_REASONS
  const [reason, setReason] = useState('')

  // Reset on every open so a reason picked for the other direction cannot leak
  // into this one — the backend would 422 on it, but the operator would have
  // seen a pre-selected answer he never gave.
  useEffect(() => {
    if (open) setReason('')
  }, [open, customerPays])

  const handleConfirm = () => {
    if (!reason || submitting) return
    onConfirm?.(reason)
  }

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="return-cost-decision-title"
    >
      <DialogTitle id="return-cost-decision-title">
        <Stack direction="row" spacing={SP.tight} alignItems="center">
          <EuroOutlinedIcon fontSize="small" color="action" />
          <span>{customerPays ? 'Customer pays return shipping' : 'Brewout pays return shipping'}</span>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={SP.card}>
          {!customerPays ? (
            <Alert severity="info" sx={{ py: 0.5 }}>
              A free return needs a reason on the case. It is recorded on the case
              timeline with a timestamp.
            </Alert>
          ) : null}

          <FormControl disabled={submitting}>
            <FormLabel sx={{ fontSize: FS.md }} id="return-cost-reason-label">
              Reason
            </FormLabel>
            <RadioGroup
              aria-labelledby="return-cost-reason-label"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            >
              {options.map((o) => (
                <FormControlLabel
                  key={o.value}
                  value={o.value}
                  control={<Radio size="small" />}
                  sx={{ alignItems: 'flex-start', mb: SP.chip }}
                  label={(
                    <Stack sx={{ pt: 0.25 }}>
                      <Typography variant="body2">{o.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {o.hint}
                      </Typography>
                    </Stack>
                  )}
                />
              ))}
            </RadioGroup>
          </FormControl>

          {errorMsg ? <Alert severity="error">{errorMsg}</Alert> : null}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <DisabledReason
          reason={submitting ? null : !reason ? 'Select a reason first.' : null}
        >
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!reason || submitting}
            startIcon={submitting ? <CircularProgress size={14} /> : null}
          >
            {submitting ? 'Saving…' : 'Save decision'}
          </Button>
        </DisabledReason>
      </DialogActions>
    </Dialog>
  )
}
